import { useState } from 'react';
import {
  autonomyCeiling,
  autonomyCeilingSet,
  autonomyProposalApprove,
  autonomyProposals,
  ceilingProposalApprove,
  freezeRelease,
  killDeactivate,
  stopsList,
  type ApiError,
  type AutonomyCeiling,
  type AutonomyLevelName,
  type AutonomyProposal,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { ErrorNote } from '../../ErrorNote';
import { noDash, stop } from './common';
import { OWNER_ONLY, useIsOwner } from '../../shell/aiControls';

// Restored from the Flutter console (autonomy_governance_card.dart, atlas_view.dart):
// the two-person approvals of a FULL-autonomy request, the Aithora platform ceiling, and
// the list of everything stopped. The V3 design's Autonomy tab has no control for these
// (FULL is locked there), so each is drawn in the tab's own style below the level, and
// each block draws nothing until its route answers.

const LEVELS: AutonomyLevelName[] = ['observe', 'suggest', 'assist', 'full'];
const HEAD = 'font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;';
const WRAP = 'min-width:0;padding:13px 0 2px;border-top:1px solid rgba(34,39,46,.14);margin-top:14px;';
const ROW = 'display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding:9px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;';
const BTN = 'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;';
const segStyle = (on: boolean) =>
  'flex:1;display:inline-flex;align-items:center;justify-content:center;height:30px;border-radius:7px;font-size:14px;font-weight:700;letter-spacing:.06em;text-decoration:none;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;border:1px solid rgba(34,39,46,.14);');

const when = (iso: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};

type Pending =
  | { kind: 'approve'; p: AutonomyProposal }
  | { kind: 'ceiling'; level: AutonomyLevelName }
  | { kind: 'freeze'; organizationId: string | null; label: string }
  | { kind: 'kill'; id: string; label: string };

/** Pending FULL requests: visible to whoever may set autonomy; Approve where the server says this person may sign. */
export function AutonomyProposals({ onChanged }: { onChanged: () => void }) {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const q = useQuery((signal) => autonomyProposals(client, { signal }), [client]);
  const [pending, setPending] = useState<AutonomyProposal | null>(null);
  const rows = q.data?.proposals ?? [];
  if (!rows.length) return null;
  return (
    <div data-testid="autonomy-proposals" style={sx(WRAP)}>
      <div style={sx(HEAD)}>Waiting for a second signature</div>
      {rows.map((p) => (
        <div key={p.id} style={sx(ROW)}>
          <span style={sx('flex:1 1 260px;min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;text-wrap:pretty;')}>{noDash(p.summary)}</span>
            <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;')}>
              {`${p.kind === 'ceiling' ? 'Platform ceiling' : 'This tenant'} · ${p.approvalsCount} of ${p.requiredApprovals} signed · ${p.stale ? 'expired with a restart, propose it again' : 'expires ' + when(p.expiresAt)}`}
            </span>
          </span>
          {p.canApprove && !p.stale ? (
            <a
              href="#"
              aria-disabled={!!roReason || undefined}
              title={roReason ?? undefined}
              onClick={(e) => {
                stop(e);
                if (!roReason) setPending(p);
              }}
              style={sx(BTN + 'background:#2F5C97;color:#fff;')}
            >
              Approve
            </a>
          ) : (
            <span style={sx('font-size:14px;color:#54606C;')}>{p.isProposer ? 'You proposed it. Someone else signs.' : p.kind === 'autonomy' ? 'Only the owner approves FULL.' : 'Needs another operator.'}</span>
          )}
        </div>
      ))}
      {pending && (
        <ConfirmDialog
          title={pending.kind === 'ceiling' ? 'Raise the platform ceiling to FULL?' : 'Approve FULL autonomy?'}
          body="Atlas may then act on the dialable, low-risk actions with no signature. Clinical, emergency and Atlas directive actions still always need a person. Sealed to the Truth Ledger under your name."
          confirmLabel="Approve"
          danger
          reason={{ label: 'Why', placeholder: 'Why this is safe now (required)', required: true }}
          onConfirm={async (why) => {
            if (pending.kind === 'ceiling') await ceilingProposalApprove(client, { params: { id: pending.id }, body: { reason: why } });
            else await autonomyProposalApprove(client, { params: { id: pending.id }, body: { reason: why } });
            q.reload();
            onChanged();
          }}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  );
}

/** Aithora's platform ceiling (Operator only) and everything stopped right now. */
export function PlatformGovernance({ onChanged }: { onChanged: () => void }) {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const ceiling = useQuery<AutonomyCeiling>((signal) => autonomyCeiling(client, { signal }), [client]);
  const stops = useQuery((signal) => stopsList(client, { signal }), [client]);
  const [pending, setPending] = useState<Pending | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);

  const cur = ceiling.data?.ceiling ?? null;
  const freezes = stops.data?.freezes ?? [];
  const kills = stops.data?.killSwitches ?? [];
  const canResume = !!stops.data?.canResume;

  const raise = (l: AutonomyLevelName) => !!cur && LEVELS.indexOf(l) > LEVELS.indexOf(cur);
  // Raising the ceiling is the owner's alone (owner by email, from the server); anyone else
  // sees the higher levels held, with the reason, instead of a 403 after confirming.
  const notOwner = useIsOwner() === false;
  const held = (l: AutonomyLevelName) => notOwner && raise(l);

  return (
    <>
      {ceiling.data && (
        <div data-testid="autonomy-ceiling" style={sx(WRAP)}>
          <div style={sx(HEAD)}>Aithora ceiling · every facility</div>
          <div role="radiogroup" aria-label="Platform autonomy ceiling" style={sx('display:flex;gap:5px;margin-top:9px;min-width:0;flex-wrap:wrap;')}>
            {LEVELS.map((l) => (
              <a
                key={l}
                href="#"
                role="radio"
                aria-checked={l === cur}
                aria-disabled={!!roReason || held(l) || undefined}
                data-testid={`ceiling-${l}`}
                title={roReason ?? (held(l) ? OWNER_ONLY : raise(l) ? 'Raising the ceiling is the owner’s alone. The server checks.' : undefined)}
                onClick={(e) => {
                  stop(e);
                  if (!roReason && !held(l) && l !== cur) setPending({ kind: 'ceiling', level: l });
                }}
                style={sx(segStyle(l === cur) + (held(l) ? 'opacity:.5;cursor:not-allowed;' : ''))}
              >
                {l.toUpperCase()}
              </a>
            ))}
          </div>
          <div style={sx('font-size:16px;color:#3C454F;line-height:1.55;margin-top:10px;max-width:86ch;text-wrap:pretty;')}>
            {noDash(ceiling.data.note)} Lowering it caps every facility at once. Raising it is the owner’s alone, and FULL needs a second signature.
          </div>
          {notOwner && <div data-testid="ceiling-owner-only" style={sx('font-size:16px;color:#54606C;margin-top:6px;')}>{`Raising it: ${OWNER_ONLY}`}</div>}
          {note && <div style={sx('font-size:16px;color:#1F6B52;margin-top:6px;')}>{note}</div>}
          {err && <ErrorNote error={err} compact />}
        </div>
      )}

      {(freezes.length > 0 || kills.length > 0) && (
        <div data-testid="atlas-stops" style={sx(WRAP)}>
          <div style={sx(HEAD)}>{`Stopped now · ${freezes.length + kills.length}`}</div>
          {freezes.map((f) => {
            const label = f.scope === 'global' ? 'Every facility' : (f.organizationName ?? 'A facility');
            return (
              <div key={'f' + (f.organizationId ?? 'global')} style={sx(ROW)}>
                <span style={sx('flex:1 1 260px;min-width:0;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{`Frozen · ${label}`}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;text-wrap:pretty;')}>
                    {[f.engagedBy ? 'by ' + f.engagedBy : null, when(f.engagedAt), f.reason].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {canResume && (
                  <a href="#" aria-disabled={!!roReason || undefined} title={roReason ?? undefined} onClick={(e) => { stop(e); if (!roReason) setPending({ kind: 'freeze', organizationId: f.organizationId, label }); }} style={sx(BTN + 'color:#12503C;background:rgba(47,138,111,.14);')}>
                    Resume
                  </a>
                )}
              </div>
            );
          })}
          {kills.map((k) => {
            const label = `${k.tenantName} · ${k.scope}${k.scopeValue ? ' ' + k.scopeValue : ''}`;
            return (
              <div key={'k' + k.id} style={sx(ROW)}>
                <span style={sx('flex:1 1 260px;min-width:0;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{`Kill switch · ${label}`}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;text-wrap:pretty;')}>{[k.activatedBy ? 'by ' + k.activatedBy : null, when(k.activatedAt), k.reason].filter(Boolean).join(' · ')}</span>
                </span>
                {canResume && (
                  <a href="#" aria-disabled={!!roReason || undefined} title={roReason ?? undefined} onClick={(e) => { stop(e); if (!roReason) setPending({ kind: 'kill', id: k.id, label }); }} style={sx(BTN + 'color:#12503C;background:rgba(47,138,111,.14);')}>
                    Resume
                  </a>
                )}
              </div>
            );
          })}
          {!canResume && <div style={sx('font-size:16px;color:#54606C;padding-top:6px;')}>Only the owner turns these back on.</div>}
        </div>
      )}

      {pending && pending.kind === 'ceiling' && (
        <ConfirmDialog
          title={`Set the platform ceiling to ${pending.level.toUpperCase()}?`}
          body={raise(pending.level)
            ? 'Every facility may then be set up to this level. ' + (pending.level === 'full' ? 'FULL waits for a second signature before it takes effect.' : 'It takes effect now.')
            : 'Every facility above this level is capped at it now.'}
          confirmLabel="Set ceiling"
          danger={raise(pending.level)}
          reason={{ label: 'Why', placeholder: pending.level === 'full' ? 'Why (required)' : 'Why (optional)', required: pending.level === 'full' }}
          onConfirm={async (why) => {
            setErr(null);
            const r = await autonomyCeilingSet(client, { body: { level: pending.level, reason: why || undefined, confirm: true } });
            setNote(r.pendingApproval ? 'Waiting for a second signature.' : null);
            ceiling.reload();
            onChanged();
          }}
          onClose={() => setPending(null)}
        />
      )}
      {pending && (pending.kind === 'freeze' || pending.kind === 'kill') && (
        <ConfirmDialog
          title={`Resume ${pending.label}?`}
          body="Atlas starts answering again there. It is sealed to the Truth Ledger under your name."
          confirmLabel="Resume"
          onConfirm={async () => {
            if (pending.kind === 'freeze') {
              await freezeRelease(client, { body: { action: 'release', scope: pending.organizationId ? 'org' : 'global', ...(pending.organizationId ? { organizationId: pending.organizationId } : {}), confirm: true } });
            } else {
              await killDeactivate(client, { body: { killSwitchId: pending.id, confirm: true } });
            }
            stops.reload();
            onChanged();
          }}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}
